import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useCertificateApi } from '../hooks/useCertificateApi';
import { CertificateResponse } from '../services/certificateApi';
import { getVerificationUrl } from '../utils/certificateVerification';
import '../styles/CertificateDetails.css';

interface CertificateDetailsProps {
  certificate: CertificateResponse;
}

const CertificateDetails: React.FC<CertificateDetailsProps> = ({ certificate }) => {
  const { isAuthenticated } = useAuth();
  const { downloading, error, downloadCertificate } = useCertificateApi();
  const verificationUrl = getVerificationUrl(certificate.certificateNumber);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'valid':
        return 'status-valid';
      case 'expired':
        return 'status-expired';
      case 'suspended':
        return 'status-suspended';
      default:
        return 'status-unknown';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'valid':
        return 'OK';
      case 'expired':
      case 'suspended':
        return '!';
      default:
        return '?';
    }
  };

  return (
    <div className="certificate-details">
      <div className="details-header">
        <div className={`status-badge ${getStatusColor(certificate.status)}`}>
          <span className="status-icon">{getStatusIcon(certificate.status)}</span>
          <span className="status-text">{certificate.status}</span>
        </div>

        {isAuthenticated && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => downloadCertificate(certificate.id, 'pdf')}
              disabled={downloading}
            >
              {downloading ? 'Preparing...' : 'Download PDF'}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => downloadCertificate(certificate.id, 'png')}
              disabled={downloading}
            >
              {downloading ? 'Preparing...' : 'Download Image'}
            </button>
          </div>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <table className="details-table">
        <tbody>
          <tr>
            <td className="label">Certificate ID:</td>
            <td className="value">{certificate.id}</td>
          </tr>
          <tr>
            <td className="label">Company Name:</td>
            <td className="value">{certificate.companyName}</td>
          </tr>
          <tr>
            <td className="label">Certificate Number:</td>
            <td className="value">{certificate.certificateNumber}</td>
          </tr>
          <tr>
            <td className="label">Standard:</td>
            <td className="value">{certificate.standard}</td>
          </tr>
          <tr>
            <td className="label">Scope:</td>
            <td className="value certificate-scope">{certificate.scope}</td>
          </tr>
          <tr>
            <td className="label">Registered Address:</td>
            <td className="value">{certificate.registeredAddress || '-'}</td>
          </tr>
          <tr>
            <td className="label">Branch Address:</td>
            <td className="value">{certificate.branchAddress || '-'}</td>
          </tr>
          <tr>
            <td className="label">Issue Date:</td>
            <td className="value">{formatDate(certificate.issueDate)}</td>
          </tr>
          <tr>
            <td className="label">Expiry Date:</td>
            <td className="value">{formatDate(certificate.expiryDate)}</td>
          </tr>
          <tr>
            <td className="label">Created Date:</td>
            <td className="value">{formatDate(certificate.createdDate)}</td>
          </tr>
        </tbody>
      </table>

      {certificate.status === 'Valid' && (
        <div className="alert alert-success">
          <strong>Certificate is Valid</strong>
          <p>This certificate is authentic and has not expired.</p>
        </div>
      )}

      {certificate.status === 'Expired' && (
        <div className="alert alert-warning">
          <strong>Certificate Expired</strong>
          <p>This certificate has expired. Please contact the issuer for renewal.</p>
        </div>
      )}

      {certificate.status === 'Suspended' && (
        <div className="alert alert-error">
          <strong>Certificate Suspended</strong>
          <p>
            This certificate has been suspended. Please contact the issuer for more
            information.
          </p>
        </div>
      )}

      <div className="qr-code-section">
        <h4>Verification Link</h4>
        <a className="qr-url" href={verificationUrl}>
          {verificationUrl}
        </a>
      </div>
    </div>
  );
};

export default CertificateDetails;
